import React from "react"
import { format, parseISO } from "date-fns"

import { Badge } from "@/components/ui/badge"

// One "Last updated" time for the page. EPR (EPR_GenerateRows) and the metrics (EPR_GenerateMetrics)
// update separately, but EPR records no "finished" time, only each row's update to the minute, so
// after a normal run the two times are the same minute. Only when the metrics are older than the
// EPR sync is the metrics time shown too, marked. The comparison is made in SQL (MetricsBehindEpr);
// this only shows it.

export const formatSyncTime = (value: string | null | undefined) =>
  value ? format(parseISO(value), "MMM d, yyyy h:mm a") : null

interface Props {
  eprSyncedAt: string | null | undefined
  metricsCalculatedAt: string | null | undefined
  metricsBehindEpr: boolean
  // false ignores the metrics, for people whose page has no Quarterly Performance section
  showMetrics?: boolean
  // admin page only: the quarter's last metrics run, when it did not succeed
  lastRunStatus?: string | null
  lastRunAt?: string | null
  className?: string
}

const SyncStatus: React.FC<Props> = ({
  eprSyncedAt,
  metricsCalculatedAt,
  metricsBehindEpr: behind,
  showMetrics = true,
  lastRunStatus,
  lastRunAt,
  className = "",
}) => {
  const metricsBehindEpr = showMetrics && behind && !!metricsCalculatedAt
  // a row only inserted so far has no EPR stamp; the metrics time stands in for it
  const lastUpdated = eprSyncedAt ?? (showMetrics ? metricsCalculatedAt : null)

  return (
    <div className={`flex flex-col gap-1 text-xs text-muted-foreground ${className}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>
          Last updated{" "}
          <span className="font-medium text-foreground">{formatSyncTime(lastUpdated) ?? "—"}</span>
        </span>
        {metricsBehindEpr && (
          <Badge className="border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-100">
            Metrics from {formatSyncTime(metricsCalculatedAt)}
          </Badge>
        )}
        {lastRunStatus === "Failed" && (
          <Badge className="border-transparent bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-100">
            Last metrics run failed{lastRunAt ? ` ${formatSyncTime(lastRunAt)}` : ""}
          </Badge>
        )}
        {lastRunStatus === "Running" && <Badge variant="secondary">Metrics are being calculated</Badge>}
      </div>
      {metricsBehindEpr && (
        <div className="text-amber-700 dark:text-amber-400">
          The metrics were calculated before the latest update, so time entries made since then are
          not counted yet. They are included the next time the metrics are calculated.
        </div>
      )}
    </div>
  )
}

export default SyncStatus
